"use client";

import { useState, useEffect } from "react";
import { Button } from "../button.js";
import { Input } from "../input.js";
import { Label } from "../label.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../dialog.js";
import { Copy, Share2, Check, ExternalLink } from "lucide-react";
import { useToast } from "../../hooks/use-toast.js";
import { useAuth } from "../../hooks/useAuth.js";

interface ShareCalendarModalProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ShareCalendarModal = ({ children, open, onOpenChange }: ShareCalendarModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = open !== undefined ? open : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;
  const [selectedEmail, setSelectedEmail] = useState("");
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Team member options
  const teamMembers = [
    { email: "eliza.keffeler@westtn.consulting", name: "Eliza Keffeler" },
    { email: "charlie.keffeler@westtn.consulting", name: "Charlie Keffeler" },
    { email: "jimmytreb@gmail.com", name: "Jimmy Turner" },
    { email: "miclainkeffeler@westtn.consulting", name: "Miclain Keffeler" },
  ];

  // Generate the shareable URL
  const generateUrl = () => {
    if (!selectedEmail) {
      toast({
        title: "Email Required",
        description: "Please select a team member to share their calendar.",
        variant: "destructive",
      });
      
return;
    }

    const baseUrl = window.location.origin;
    const publicBookingUrl = `${baseUrl}/crm/calendar/public-booking`;
    const params = new URLSearchParams({
      email: selectedEmail,
      duration: meetingDuration.toString(),
    });

    const fullUrl = `${publicBookingUrl}?${params.toString()}`;
    setGeneratedUrl(fullUrl);
  };

  // Copy URL to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast({
        title: "URL Copied",
        description: "The calendar booking URL has been copied to your clipboard.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy URL to clipboard. Please copy manually.",
        variant: "destructive",
      });
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setGeneratedUrl("");
      setCopied(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children && (
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Calendar for Booking
          </DialogTitle>
          <DialogDescription>
            Generate a shareable URL that allows clients to book meetings with your team members.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="teamMember">Team Member *</Label>
            <Select value={selectedEmail} onValueChange={setSelectedEmail}>
              <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                {teamMembers.map((member) => (
                  <SelectItem key={member.email} value={member.email} className="hover:bg-card-50 focus:bg-card-50">
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="duration">Default Meeting Duration</Label>
            <Select value={meetingDuration.toString()} onValueChange={(value: string) => setMeetingDuration(parseInt(value))}>
              <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                <SelectItem value="15" className="hover:bg-card-50 focus:bg-card-50">15 minutes</SelectItem>
                <SelectItem value="30" className="hover:bg-card-50 focus:bg-card-50">30 minutes</SelectItem>
                <SelectItem value="45" className="hover:bg-card-50 focus:bg-card-50">45 minutes</SelectItem>
                <SelectItem value="60" className="hover:bg-card-50 focus:bg-card-50">1 hour</SelectItem>
                <SelectItem value="90" className="hover:bg-card-50 focus:bg-card-50">1.5 hours</SelectItem>
                <SelectItem value="120" className="hover:bg-card-50 focus:bg-card-50">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={generateUrl} className="w-full" disabled={!selectedEmail}>
            Generate Booking URL
          </Button>

          {generatedUrl && (
            <div className="space-y-3">
              <div>
                <Label>Shareable Booking URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={generatedUrl}
                    readOnly
                    className="flex-1 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyToClipboard}
                    className="px-3"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(generatedUrl, '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Test URL
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setGeneratedUrl("");
                    setSelectedEmail("");
                    setMeetingDuration(30);
                  }}
                >
                  Generate New
                </Button>
              </div>

              <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
                <p className="font-medium mb-1">How to use this URL:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Share this URL with clients via email, text, or your website</li>
                  <li>Clients can book meetings without creating an account</li>
                  <li>The URL will pre-select the team member and default duration</li>
                  <li>Clients will see available time slots and can book directly</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
